import { useEffect, useRef } from 'react';
import { useStore } from '~/stores';
import { SyncServiceConfig } from '~/services/sync';
import { isDemoMode, activateDemoMode } from '~/services/demo/DemoModeService';
import { setNativeDeviceCredentials } from '~/services/platform/kioskMode';
import deviceTokenService from '~/services/auth/DeviceToken';
import { resolverDeviceId } from '~/services/auth/deviceIdGuard';
import {
  resolveApiBaseUrl,
  resolveSseBaseUrl,
} from '~/services/embedded/hostConfig';

// Environment config with defaults
const getConfig = (authDeviceId: string): SyncServiceConfig => {
  const storedApiKey =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('efflux_device_api_key')
      : null;

  return {
    contentApiUrl: resolveApiBaseUrl(),
    // O id é SEMPRE o do authStore (vem do `getHardwareId()` da plataforma, com
    // prefixo). O fallback anterior era `efflux-${Date.now()}`: um id sem
    // prefixo e DIFERENTE A CADA BOOT, que criaria um player fantasma por
    // reinício se um dia o guard acima fosse removido. Vide `deviceIdGuard`.
    deviceId:
      resolverDeviceId(authDeviceId, import.meta.env.VITE_DEVICE_ID) ??
      authDeviceId,
    // 2026-05-24 — Antes era hardcoded '1.0.0'. Backend recebia 1.0.0 de
    // todos os players independente da versão real, e a coluna "Versão APP"
    // da tabela de Players ficava sem variação útil. Agora usa
    // VITE_APP_VERSION injetada pelo vite.config.ts (lendo package.json
    // no build-time). Fallback '0.0.0' pra build sem injeção (vitest etc),
    // que é diferente de '1.0.0' pra ficar fácil identificar problema.
    clientVersion:
      (import.meta.env.VITE_APP_VERSION as string | undefined) || '0.0.0',
    pollingIntervalMs: parseInt(
      import.meta.env.VITE_SYNC_INTERVAL_MS || '300000',
      10,
    ),
    apiKey: import.meta.env.VITE_DEVICE_API_KEY || storedApiKey || undefined,

    // SSE Configuration
    sseApiUrl: resolveSseBaseUrl(),
    sseEnabled: import.meta.env.VITE_SSE_ENABLED !== 'false',
    sseConnectedPollingIntervalMs: parseInt(
      import.meta.env.VITE_SSE_CONNECTED_POLLING_INTERVAL_MS || '1800000',
      10,
    ), // 30 minutes
  };
};

/**
 * Hook to initialize and manage the sync service.
 * Should be called once at the app root level.
 *
 * IMPORTANT: Waits for auth to complete before initializing to avoid
 * using wrong device ID from environment variables.
 */
export const useSyncInit = () => {
  const { syncStore, schedulerStore, mediaStore, streamStore, authStore } =
    useStore();
  const initialized = useRef(false);

  useEffect(() => {
    // Demo mode: bypass all sync/backend initialization
    if (isDemoMode()) {
      activateDemoMode({ schedulerStore, syncStore, streamStore });
      return;
    }

    // Only initialize once
    if (initialized.current) {
      return () => {}; // Cleanup vazio para manter consistência de hooks
    }

    // OFFLINE-FIRST BEHAVIOR:
    // Initialize sync service regardless of auth status to enable offline playback
    // This allows the TV to load cached content even when server is unreachable

    // WAIT FOR AUTH STORE TO RESOLVE HARDWARE ID
    // We strictly avoid temporary IDs to prevent database pollution.
    if (!authStore.deviceId) {
      console.log('[useSyncInit] Waiting for hardware ID resolution...');
      return;
    }

    const deviceId = authStore.deviceId;

    const config = getConfig(deviceId);

    initialized.current = true;

    console.log('[useSyncInit] Initializing sync service (Definitive ID):', {
      contentApiUrl: config.contentApiUrl,
      deviceId: config.deviceId,
      isAuthenticated: authStore.isAuthenticated,
      registrationStatus: authStore.registrationStatus,
      note: 'Using authenticated device ID',
      pollingIntervalMs: config.pollingIntervalMs,
      sseApiUrl: config.sseApiUrl,
      sseEnabled: config.sseEnabled,
    });

    // Configure AnalyticsService with the correct API URL and Device ID
    import('~/services/analytics/AnalyticsService')
      .then(({ default: analyticsService }) => {
        analyticsService.setApiUrl(config.contentApiUrl);
        analyticsService.setDeviceId(config.deviceId);
        // Fase 1b — quando o server reportar sse_connected=false no flush de
        // analytics, reconecta o SSE de conteúdo (callback lazy: o syncService
        // pode ainda não existir aqui, mas existirá quando for invocado).
        analyticsService.setSseReconnectHint(() =>
          syncStore.ensureSSEConnected(),
        );
      })
      .catch((err) =>
        console.error(
          '[useSyncInit] Failed to configure AnalyticsService:',
          err,
        ),
      );

    // Espelha credenciais no SharedPreferences nativo (Android Device Owner).
    // Necessário pra ScreenOffReceiver reportar tampering events fora do WebView.
    // No-op em iOS / web / Tizen / webOS.
    const deviceToken = deviceTokenService.getTokenOrNull();
    if (deviceToken) {
      void setNativeDeviceCredentials(
        config.deviceId,
        deviceToken,
        config.contentApiUrl,
      );
    }

    // Initialize sync store
    // This loads cached metadata and allows offline playback
    syncStore.init(config, {
      schedulerStore,
      mediaStore,
      streamStore,
      authStore,
    });

    // Start polling (will be skipped automatically if not authenticated)
    syncStore.startPolling();

    // If device is already authenticated, trigger immediate sync
    if (authStore.isAuthenticated && authStore.deviceId) {
      console.log(
        '[useSyncInit] Device already authenticated, triggering immediate sync',
      );
      // Small delay to ensure everything is initialized
      setTimeout(() => {
        syncStore.sync();
      }, 200);
    } else {
      console.log('[useSyncInit] Device not authenticated yet:', {
        isAuthenticated: authStore.isAuthenticated,
        registrationStatus: authStore.registrationStatus,
        note: 'Will sync when authenticated, but cached content can play now',
      });
    }

    // Cleanup on unmount
    return () => {
      syncStore.stopPolling();
    };
  }, [
    syncStore,
    schedulerStore,
    mediaStore,
    streamStore,
    authStore,
    authStore.isAuthenticated,
    authStore.deviceId,
  ]);

  // React to device ID changes (e.g. after auth completes or restores from storage)
  // This ensures we switch from temporary ID to real hardware ID without restart
  useEffect(() => {
    if (isDemoMode()) return;

    if (authStore.deviceId && !authStore.deviceId.startsWith('efflux-temp-')) {
      console.log(
        '[useSyncInit] Updating services with authenticated device ID:',
        authStore.deviceId,
      );

      // 1. Update SyncService (and SSE)
      syncStore.updateDeviceId(authStore.deviceId);

      // 2. Update AnalyticsService
      import('~/services/analytics/AnalyticsService')
        .then(({ default: analyticsService }) => {
          analyticsService.setDeviceId(authStore.deviceId);
        })
        .catch((err) =>
          console.error(
            '[useSyncInit] Failed to update AnalyticsService device ID:',
            err,
          ),
        );

      // 3. Re-espelha credenciais nativas (pra ScreenOffReceiver)
      const apiUrl = resolveApiBaseUrl();
      const deviceToken = deviceTokenService.getTokenOrNull();
      if (deviceToken) {
        void setNativeDeviceCredentials(
          authStore.deviceId,
          deviceToken,
          apiUrl,
        );
      }
    }
  }, [authStore.deviceId, syncStore]);

  return {
    status: syncStore.status,
    isOnline: syncStore.isOnline,
    lastSyncTime: syncStore.lastSyncTimeFormatted,
    sync: () => syncStore.sync(),
  };
};

export default useSyncInit;
